﻿interface DotNetObject {
    invokeMethod<T>(methodIdentifier: string, ...args: any[]): T;
    invokeMethodAsync<T>(methodIdentifier: string, ...args: any[]): Promise<T>;
    dispose(): void;
}

class App {
    // For additional details, see the JsBridge.cs file.
    private static jsBridgeObj: DotNetObject;
    private static crystaHeaderScrollHandlers = new Map<string, EventListener>();
    public static registerJsBridge(dotnetObj: DotNetObject) {
        App.jsBridgeObj = dotnetObj;
        App.jsBridgeObj.invokeMethodAsync('SetIsMobileBrowser', App.isMobileBrowser());
    }

    public static showDiagnostic() {
        return App.jsBridgeObj?.invokeMethodAsync('ShowDiagnostic');
    }

    public static publishMessage(message: string, payload: any) {
        return App.jsBridgeObj?.invokeMethodAsync('PublishMessage', message, payload);
    }

    public static getTimeZone(): string {
        return Intl.DateTimeFormat().resolvedOptions().timeZone;
    }

    public static openDevTools() {
        const allScripts = Array.from(document.scripts).map(s => s.src);
        const scriptAppended = allScripts.find(as => as.includes('npm/eruda'));

        if (scriptAppended) {
            (window as any).eruda.show();
            return;
        }

        const script = document.createElement('script');
        script.src = "https://cdn.jsdelivr.net/npm/eruda";
        document.body.append(script);
        script.onload = function () {
            (window as any).eruda.init();
            (window as any).eruda.show();
        }
    }

    /* Checks for and applies updates if available.
      Called by `WebAppUpdateService.cs` when the user clicks the app version in `AppShell.razor`
      or when `ForceUpdateSnackbar.razor` appears after a forced update. */
    public static async tryUpdatePwa(autoReload: boolean) {
        const bswup = (window as any).BitBswup; // https://bitplatform.dev/bswup
        if (!bswup) return;

        if (autoReload) {
            if (await bswup.skipWaiting()) return; // Use new service worker if available and reload the page.
        }

        const bswupProgress = (window as any).BitBswupProgress;
        if (!bswupProgress) return;

        bswupProgress.config({ autoReload });

        bswup.checkForUpdate();
    }

    public static isDesktopDevice(): boolean {
        const isLargeScreen = window.innerWidth >= 768;
        return isLargeScreen;
    }

    // Tracks named OAuth popups opened by CrystaCode so we can check if they are still open.
    private static _oauthPopups: Record<string, Window | null> = {};

    public static openTrackedPopup(url: string, name: string, features: string): void {
        const popup = window.open(url, name, features);
        App._oauthPopups[name] = popup;
        
        // Handle popup block
        if (!popup || popup.closed || typeof popup.closed === 'undefined') {
            if (url && url !== "about:blank") {
                // fallback: redirect main tab to URL if popup is blocked
                window.location.href = url;
            }
        }
    }

    public static updateTrackedPopupUrl(name: string, url: string): void {
        const popup = App._oauthPopups[name];
        if (popup && !popup.closed) {
            popup.location.href = url;
        } else {
            // If popup was blocked or closed in the meantime, fallback to redirecting the main tab
            window.location.href = url;
        }
    }

    public static closeTrackedPopup(name: string): void {
        const popup = App._oauthPopups[name];
        if (popup && !popup.closed) {
            popup.close();
        }
        App._oauthPopups[name] = null;
    }

    public static isPopupOpen(name: string): boolean {
        const popup = App._oauthPopups[name];
        return popup !== null && popup !== undefined && !popup.closed;
    }
    public static scrollToEnd(elementId: string): void {
        const element = document.getElementById(elementId);
        if (element) {
            element.scrollTo({
                top: element.scrollHeight,
                behavior: 'smooth'
            });
        }
    }

    public static scrollToTop(): void {
        const element = document.querySelector('.stack') as HTMLElement;
        if (element) {
            element.scrollTo({
                top: 0,
                behavior: 'smooth'
            });
        }
    }

    public static scrollToLeft(elementId: string): void {
        const element = document.getElementById(elementId);
        if (element) {
            element.scrollTo({
                left: 0,
                behavior: 'smooth'
            });
        }
    }
    public static detectScrollBottom(dotNetHelper: any): void {
        window.addEventListener('scroll', () => {
            const scrollTop = window.scrollY;
            const windowHeight = window.innerHeight;
            const documentHeight = document.body.scrollHeight;

            const isBottom = scrollTop + windowHeight >= documentHeight;
            dotNetHelper.invokeMethodAsync('OnScrollBottomReached', isBottom);
        });
    }

    public static isIOS(): boolean {
        const userAgent = window.navigator.userAgent.toLowerCase();
        const platform = (window.navigator.platform || '').toLowerCase();

        // Check for iPhone, iPad, iPod in user agent or platform
        const isIOSDevice = /iphone|ipad|ipod/.test(userAgent) || /iphone|ipad|ipod/.test(platform);

        // Check for iPad on iOS 13+ (reports as MacIntel but has touch support)
        const isIPadOS = platform === 'macintel' && navigator.maxTouchPoints > 1;

        return isIOSDevice || isIPadOS;
    }

    public static async copyText(text: string): Promise<boolean> {
        const str = String(text ?? "");

        // iOS detection (covers iPadOS on Mac hardware too)
        const isIOS =
            /iPad|iPhone|iPod/.test(navigator.userAgent) ||
            (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);

        const fallbackExec = (): boolean => {
            let ta: HTMLTextAreaElement | null = null;
            try {
                ta = document.createElement("textarea");
                ta.value = str;
                ta.setAttribute("readonly", "");
                ta.style.position = "fixed";
                ta.style.top = "0";
                ta.style.left = "0";
                ta.style.opacity = "0";
                ta.style.pointerEvents = "none";
                document.body.appendChild(ta);

                ta.focus();
                ta.select();
                ta.setSelectionRange(0, ta.value.length); // iOS quirk

                return document.execCommand("copy");
            } finally {
                if (ta && ta.parentNode) ta.parentNode.removeChild(ta);
            }
        };

        // On iOS, prefer the synchronous fallback to preserve user activation
        if (isIOS) {
            return fallbackExec();
        }

        // Other platforms: try async Clipboard API, then fallback
        try {
            if (navigator.clipboard && typeof navigator.clipboard.writeText === "function") {
                await navigator.clipboard.writeText(str);
                return true;
            }
        } catch {
            // ignore and try fallback
        }
        return fallbackExec();

    }

    // NEW: synchronous variant for use with Blazor WASM in-process JS interop (needed for iOS user-gesture timing)
    public static copyTextSync(text: string): boolean {
        const str = String(text ?? "");
        const isIOS =
            /iPad|iPhone|iPod/.test(navigator.userAgent) ||
            (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);

        const fallbackExec = (): boolean => {
            let ta: HTMLTextAreaElement | null = null;
            try {
                ta = document.createElement("textarea");
                ta.value = str;
                ta.setAttribute("readonly", "");
                ta.style.position = "fixed";
                ta.style.top = "0";
                ta.style.left = "0";
                ta.style.opacity = "0";
                ta.style.pointerEvents = "none";
                document.body.appendChild(ta);

                ta.focus();
                ta.select();
                ta.setSelectionRange(0, ta.value.length); // iOS quirk

                return document.execCommand("copy");
            } finally {
                if (ta && ta.parentNode) ta.parentNode.removeChild(ta);
            }
        };

        // If not iOS we can try navigator.clipboard.writeText but only asynchronously; since this is sync, just do fallback.
        // For iOS (primary target) fallbackExec already handles.
        return fallbackExec();
    }


    public static copyToClipboard(text: string): void {
        // iOS: use synchronous fallback to preserve user activation
        if (App.isIOS()) {
            App.fallbackCopy(text);
            return;
        }

        if (navigator.clipboard && window.isSecureContext) {
            navigator.clipboard.writeText(text).catch(err => {
                console.error("Clipboard write failed:", err);
                App.fallbackCopy(text);
            });
        } else {
            App.fallbackCopy(text);
        }
    }

    private static fallbackCopy(text: string): void {
        const textarea = document.createElement("textarea");
        textarea.value = text;

        // iOS quirks: prevent keyboard + require explicit range selection
        textarea.setAttribute("readonly", "");
        textarea.style.position = "fixed";
        textarea.style.top = "0";
        textarea.style.left = "0";
        textarea.style.opacity = "0";
        textarea.style.pointerEvents = "none";

        document.body.appendChild(textarea);
        textarea.focus();
        textarea.select();
        try {
            textarea.setSelectionRange(0, textarea.value.length); // critical for iOS
        } catch {
            // no-op
        }

        try {
            const ok = document.execCommand("copy");
            if (!ok) console.warn("Fallback copy returned false.");
        } catch (err) {
            console.error("Fallback copy failed:", err);
            throw err;
        } finally {
            document.body.removeChild(textarea);
        }
    }

    // Attach a synchronous click handler on iOS so copy runs within the user gesture (important for Blazor Server/Hybrid)
    public static attachCopyHandler(elementId: string, text: string, dotNetHelper: any): void {
        if (!App.isIOS()) return; // Only needed for iOS
        const el = document.getElementById(elementId);
        if (!el) return;

        const handler = (ev: Event) => {
            const str = String(text ?? "");
            const report = (ok: boolean) => {
                try { dotNetHelper?.invokeMethodAsync('OnCopyResult', ok); } catch { /* ignore */ }
            };

            // First try the modern API within the gesture
            if (navigator.clipboard && typeof navigator.clipboard.writeText === "function") {
                navigator.clipboard.writeText(str)
                    .then(() => report(true))
                    .catch(() => {
                        // Fallback to execCommand path
                        const ok = App.copyTextSync(str);
                        report(ok);
                    });
            } else {
                const ok = App.copyTextSync(str);
                report(ok);
            }
            // Do not prevent default; allow Blazor handlers to run too.
        };
        // Store handler for later removal
        (el as any).__copyHandler = handler;
        el.addEventListener('click', handler as EventListener, { capture: true });
        el.addEventListener('touchend', handler as EventListener, { capture: true });
    }

    public static detachCopyHandler(elementId: string): void {
        const el = document.getElementById(elementId) as any;
        if (!el || !el.__copyHandler) return;
        const handler = el.__copyHandler as EventListener;
        el.removeEventListener('click', handler, { capture: true } as any);
        el.removeEventListener('touchend', handler, { capture: true } as any);
        delete el.__copyHandler;
    }

    // Downloads a QR code svg as a PNG, falling back to the raw svg when rasterising is unavailable.
    public static downloadQrCode(svgMarkup: string, fileName: string): void {
        if (!svgMarkup) return;

        const baseName = (fileName || "qr-code").replace(/\.(svg|png)$/i, "");
        const saveAs = (href: string, name: string) => {
            const link = document.createElement("a");
            link.href = href;
            link.download = name;
            link.style.display = "none";
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        };

        // The QR svg only carries a viewBox, so give it an explicit size before rasterising.
        const sizedSvg = svgMarkup.replace(/<svg\b([^>]*)>/i, '<svg$1 width="1024" height="1024">');
        const svgUrl = URL.createObjectURL(new Blob([sizedSvg], { type: "image/svg+xml;charset=utf-8" }));
        const saveSvg = () => saveAs(svgUrl, `${baseName}.svg`);

        const image = new Image();
        image.onload = () => {
            try {
                const size = 1024;
                const canvas = document.createElement("canvas");
                canvas.width = size;
                canvas.height = size;
                const context = canvas.getContext("2d");
                if (!context) {
                    saveSvg();
                    return;
                }
                context.drawImage(image, 0, 0, size, size);
                saveAs(canvas.toDataURL("image/png"), `${baseName}.png`);
            } catch {
                saveSvg();
            } finally {
                URL.revokeObjectURL(svgUrl);
            }
        };
        image.onerror = () => {
            saveSvg();
            URL.revokeObjectURL(svgUrl);
        };
        image.src = svgUrl;
    }

    public static initScrollDownIndicator(scrollElementId: string, indicatorId: string) {
        const scrollElement = document.getElementById(scrollElementId);
        const indicator = document.getElementById(indicatorId) as HTMLElement;

        if (!scrollElement || !indicator) return;

        scrollElement.addEventListener('scroll', () => {
            if (scrollElement.scrollTop > 20) {
                indicator.classList.add('hide');
            } else {
                indicator.classList.remove('hide');
            }
        });
    }

    public static initCrystaHeaderTransition(scrollElementId: string, headerId: string, threshold: number = 24): void {
        const scrollElement = document.getElementById(scrollElementId);
        const header = document.getElementById(headerId);

        if (!scrollElement || !header) return;

        App.disposeCrystaHeaderTransition(scrollElementId, headerId);

        let lastScrollTop = scrollElement.scrollTop;

        const updateHeaderState = (): void => {
            const current = scrollElement.scrollTop;

            // 1. Base threshold logic
            if (current > threshold) {
                header.classList.add('is-scrolled');
            } else {
                header.classList.remove('is-scrolled');
                header.classList.remove('hide');
                header.classList.add('show');
            }

            // 2. Only apply hide/show when is-scrolled is active
            if (header.classList.contains('is-scrolled')) {
                if (current > lastScrollTop) {
                    // scrolling down
                    header.classList.add('hide');
                    header.classList.remove('show');
                } else {
                    // scrolling up
                    header.classList.remove('hide');
                    header.classList.add('show');
                }
            }

            lastScrollTop = current <= 0 ? 0 : current;
        };

        updateHeaderState();

        const onScroll: EventListener = () => updateHeaderState();
        scrollElement.addEventListener('scroll', onScroll, { passive: true });
        App.crystaHeaderScrollHandlers.set(headerId, onScroll);
    }

    public static disposeCrystaHeaderTransition(scrollElementId: string, headerId: string): void {
        const scrollElement = document.getElementById(scrollElementId);
        const scrollHandler = App.crystaHeaderScrollHandlers.get(headerId);

        if (scrollElement && scrollHandler) {
            scrollElement.removeEventListener('scroll', scrollHandler);
        }

        App.crystaHeaderScrollHandlers.delete(headerId);

        const header = document.getElementById(headerId);
        header?.classList.remove('is-scrolled');
        header?.classList.remove('hide');
        header?.classList.add('show');
    }

    public static isMobileBrowser(): boolean {
        const userAgent = navigator.userAgent || navigator.vendor || (window as any).opera;
        return /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i.test(userAgent.toLowerCase());
    }

    public static isAndroidOrWindows(): boolean {
        const ua = navigator.userAgent;
        const platform = navigator.platform || '';

        const isAndroid = /Android/i.test(ua);

        const isWindows = /Win/i.test(platform) || /Windows/i.test(ua);

        return isWindows || isAndroid;
    }

}

if (navigator.userAgent.includes("Edg")) {
    document.body.classList.add("edge-browser");
}

if (navigator.userAgent.includes("Firefox")) {
    document.body.classList.add("firefox-browser");
}

if (App.isAndroidOrWindows() == false) {
    document.body.classList.add("iphone-device");
}


// keep this so window.App is available for JS interop
(window as any).App = App;
